// Liquidations list widget. Same architecture as Whales: body = list,
// header (LiquidationsHeader named export) holds sort/filter/coin.

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { sortLiqs } from '../../../hooks/useSortModes';
import { useWidgetSetting } from '../cockpitContext.jsx';
import { useCoinTrades } from '../data/useCoinTrades';
import { formatPrice } from '../data/sharedLivePrices';
import {
  VirtualList, LiquidationRow, FilterDropdown, SortToggle, FILTER_OPTIONS,
} from '../../CoinTerminalSections/sectionHelpers';
import CoinPickerHeader from './CoinPickerHeader';

const EXCH_ABBR = (exchange) => {
  const map = { BINANCE: 'BN', BYBIT: 'BY', OKX: 'OX', HYPERLIQUID: 'HL', COINBASE: 'CB' };
  return map[exchange?.toUpperCase()] || exchange?.slice(0, 2) || '??';
};

function LiquidationsWidgetInstance({ instanceId, defaultCoin, onOpenTrade }) {
  const { t } = useTranslation();
  const [coin] = useWidgetSetting(instanceId, 'coin', defaultCoin);
  const [liqFilter] = useWidgetSetting(instanceId, 'filter', 0);
  const [sortMode] = useWidgetSetting(instanceId, 'sort', 'time');
  const { liquidations } = useCoinTrades(coin);

  const liqs = useMemo(() => {
    const filtered = liqFilter > 0 ? liquidations.filter((l) => l.dollarSize >= liqFilter) : liquidations;
    return sortLiqs(filtered, sortMode);
  }, [liquidations, liqFilter, sortMode]);

  if (liqs.length === 0) {
    return <div className="ct-section-content"><div className="ct-empty">{t('chartPage.empty.liquidations', 'No liquidations matching filter')}</div></div>;
  }
  return (
    <VirtualList
      items={liqs}
      rowHeight={48}
      getKey={(l, i) => l._id || `${l.timestamp}:${l.exchange}:${l.price}:${i}`}
      renderRow={(liq) => (
        <LiquidationRow
          liq={liq}
          coin={coin}
          formatPrice={formatPrice}
          markLabel={t('chartPage.liqPrice', 'Liq price')}
          exchAbbr={EXCH_ABBR}
          onOpen={() => onOpenTrade?.({ ...liq, isLiquidation: true })}
        />
      )}
    />
  );
}

export const LiquidationsHeader = React.memo(function LiquidationsHeader({ instanceId, defaultCoin }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useWidgetSetting(instanceId, 'filter', 0);
  const [sortMode, setSortMode] = useWidgetSetting(instanceId, 'sort', 'time');
  const toggleSort = () => setSortMode((m) => (m === 'time' ? 'size' : 'time'));
  return (
    <>
      <SortToggle
        mode={sortMode}
        onToggle={toggleSort}
        labelTime={t('chartPage.sortByTime', 'Sort by time')}
        labelSize={t('chartPage.sortBySize', 'Sort by size')}
      />
      <FilterDropdown icon="liq" ariaLabel="Liquidation size filter" tooltip="Фильтр по размеру ликвидации" options={FILTER_OPTIONS.liq} value={filter} onChange={setFilter} />
      <CoinPickerHeader instanceId={instanceId} defaultCoin={defaultCoin} />
    </>
  );
});

export default React.memo(LiquidationsWidgetInstance);
